import { createAction, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import { GRID_ITEM_RATIO, GRID_VIEW, MAX_DISPLAY_PARTICIPANTS_IN_GRID } from '../../constants';
import { calcMaxDisplayParticipantsInGridMobile } from '../../helpers/calc-max-display-participants-in-grid-mobile';
import { calcMaxDisplayParticipants } from './helpers/calc-max-display-participants';

type VideoGridState = {
    activeGridView: GRID_VIEW;
    currentPage: number;
    gridItemRatio: GRID_ITEM_RATIO;
    userPreferredGridView: GRID_VIEW;
    maxDisplayParticipants: number;
    videoContainerWidth: null | number;
    videoContainerHeight: null | number;
};

const initialState: VideoGridState = {
    activeGridView: GRID_VIEW.DEFAULT,
    currentPage: 0,
    gridItemRatio: GRID_ITEM_RATIO.DEFAULT,
    maxDisplayParticipants: MAX_DISPLAY_PARTICIPANTS_IN_GRID,
    userPreferredGridView: GRID_VIEW.DEFAULT,
    videoContainerWidth: null,
    videoContainerHeight: null,
};

const videoGridSlice = createSlice({
    name: 'videoGrid',
    initialState,
    reducers: {
        resetVideoGrid(state) {
            return {
                ...initialState,
                userPreferredGridView: state.userPreferredGridView,
                activeGridView: state.userPreferredGridView,
                maxDisplayParticipants: state.maxDisplayParticipants,
            };
        },
        setMaxDisplayParticipants(state, action: PayloadAction<number>) {
            state.maxDisplayParticipants = action.payload;
        },
        setStoreUserPreferredGridView(
            state,
            action: PayloadAction<{
                gridView: GRID_VIEW;
            }>
        ) {
            state.userPreferredGridView = action.payload.gridView;
        },
        setStoreGridItemRatio(
            state,
            action: PayloadAction<{
                gridItemRatio: GRID_ITEM_RATIO;
            }>
        ) {
            state.gridItemRatio = action.payload.gridItemRatio;
        },

        setCurrentPage(state, action: PayloadAction<number>) {
            state.currentPage = action.payload;
        },
        setStoreActiveGridView(
            state,
            action: PayloadAction<{
                gridView: GRID_VIEW;
                isMobile?: boolean;
                isNewGrid?: boolean;
            }>
        ) {
            state.activeGridView = action.payload.gridView;
            state.maxDisplayParticipants =
                state.activeGridView === GRID_VIEW.DEFAULT
                    ? action.payload.isMobile
                        ? calcMaxDisplayParticipantsInGridMobile()
                        : MAX_DISPLAY_PARTICIPANTS_IN_GRID
                    : calcMaxDisplayParticipants({
                          isNewGrid: action.payload.isNewGrid,
                          isMobile: action.payload.isMobile,
                          containerWidth: state.videoContainerWidth,
                      });
            state.currentPage = 0;
        },
        setVideoContainerSize(
            state,
            action: PayloadAction<{
                width: number | null;
                height: number | null;
            }>
        ) {
            const { width, height } = action.payload;

            state.videoContainerWidth = width;
            state.videoContainerHeight = height;
        },
    },
});
export const setUserPreferredGridViewAction = createAction<{ gridView: GRID_VIEW }>(
    'conference/videoGrid/setUserPreferredGridView'
);
export const setCurrentGridPageAction = createAction<number>('conference/videoGrid/setCurrentGridPage');
export const nextGridPageAction = createAction('conference/videoGrid/nextGridPage');
export const prevGridPageAction = createAction('conference/videoGrid/prevGridPage');

export const {
    setCurrentPage,
    setStoreActiveGridView,
    resetVideoGrid,
    setStoreUserPreferredGridView,
    setMaxDisplayParticipants,
    setStoreGridItemRatio,
    setVideoContainerSize,
} = videoGridSlice.actions;

export const videoGridReducer = videoGridSlice.reducer;
